/**
 * @Relay no-runtime
 */
/**
 * @graphql agg
 **/

import { useMemo } from 'react';
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import OriginTracing from '@atlassiansox/origin-tracing';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';

import type {
	usePlanSelectionPageInfoQuery as usePlanSelectionPageInfoQuery$data,
	usePlanSelectionPageInfoQueryVariables as usePlanSelectionPageInfoQuery$variables,
} from '../__types__/usePlanSelectionPageInfoQuery.ts';

const getCommerceUrlWithAltOrigin = (experienceUrl?: string | null | undefined) => {
	if (!experienceUrl) {
		return '';
	}
	const origin = new OriginTracing({ product: 'confluence' });
	const url = origin.addToUrl(experienceUrl);

	return url.toString();
};

export function usePlanSelectionPageInfo({
	skip = false,
}: {
	skip?: boolean;
} = {}) {
	const { cloudId } = useSessionData();

	const { data, error, loading } = useQuery<
		usePlanSelectionPageInfoQuery$data,
		usePlanSelectionPageInfoQuery$variables
	>(
		graphql`
			query usePlanSelectionPageInfoQuery($cloudId: ID!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						... on CommerceEntitlementInfoCcp {
							entitlementId
							entitlement {
								... on CcpEntitlement {
									id
								}
								experienceCapabilities {
									... on CcpEntitlementExperienceCapabilities {
										changeOfferingV2 {
											isAvailableToUser
											experienceUrl
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: { cloudId },
			skip: !cloudId || skip,
			client: getAGGClient(),
		},
	);

	const changeOfferingV2 =
		data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.experienceCapabilities
			?.changeOfferingV2;

	const isAvailableToUser = changeOfferingV2?.isAvailableToUser;
	const changeOfferingExperienceUrl = changeOfferingV2?.experienceUrl;

	const planSelectionPageUrl = useMemo(() => {
		return getCommerceUrlWithAltOrigin(changeOfferingExperienceUrl);
	}, [changeOfferingExperienceUrl]);

	if (!cloudId || skip) {
		return {
			isAvailableToUser: false,
			planSelectionPageUrl: undefined,
			loading: false,
			error: null,
		};
	}

	return {
		isAvailableToUser,
		planSelectionPageUrl,
		loading,
		error,
	};
}
